import React, { useCallback, useEffect, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ProductTileProps } from './ProductTile.types';
import { useProductTile as useProductTileV1 } from '../ProductTile/useProductTile';
import { useMobileProductQuickAddDrawerProvider } from './components/MobileProductQuickAddDrawer';
import { CatalogProductTileItem } from '@apps/registry/common/components/Catalog/Catalog.types';
import { findMatchingProductByDimension } from '../../utils/colorSwatchFeature';

export const useProductTile = (props: ProductTileProps) => {
  const { index, product, shouldOpenPdp } = props;

  // necessary to retain the parent product item after of all the Product state updates when hovering or selecting products via the quick add component
  const [initiallySelectedProduct, setInitiallySelectedProduct] = useState<CatalogProductTileItem>(product);

  const { handleChangeCurrentProductIndex, handleChangeCurrentProductId, openDrawer, closeDrawer } = useMobileProductQuickAddDrawerProvider();
  const {
    handleClick,
    changeActiveDimensionOption,
    handleDetailClick: handleDetailClickV1,
    handleDetailKeyPress,
    handleOnViewMoreDimensionClick,
    isAddLoading,
    isFullCover,
    productDisplayFields,
    setProductDimension,
    productPrice,
    selectedProduct,
    swatchColorOptions,
    showColorSwatch,
    isTypeCashOrCharity
  } = useProductTileV1(props);

  const handleMobileQuickAddClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, selectedOptionId?: string) => {
      e.stopPropagation();
      const selectedOptionDimension = selectedOptionId ? findMatchingProductByDimension({ parentProduct: selectedProduct, dimensionOptionId: selectedOptionId }) : null;
      const selectedProductId = selectedOptionDimension ? selectedOptionDimension.id : selectedProduct.id;

      handleChangeCurrentProductIndex(index);
      handleChangeCurrentProductId(selectedProductId);
      openDrawer();
    },
    [handleChangeCurrentProductId, handleChangeCurrentProductIndex, index, openDrawer, selectedProduct]
  );

  const handleDetailClick = useEventCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    const isDirectClick = !e.ctrlKey && !e.metaKey;
    if (isDirectClick) {
      e.preventDefault();
    }
    closeDrawer();
    handleDetailClickV1(e, !isDirectClick || !shouldOpenPdp);
  });

  const handleOnCashFundProductClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    closeDrawer();
    handleClick(e);
  });

  useEffect(() => {
    if (product.tilePresentation?.presentationType === 'parentDetails') {
      setInitiallySelectedProduct(product);
    }
  }, [product]);

  return {
    changeActiveDimensionOption,
    handleDetailClick,
    handleDetailKeyPress,
    handleMobileQuickAddClick,
    handleOnCashFundProductClick,
    handleOnViewMoreDimensionClick,
    isAddLoading,
    isFullCover,
    productDisplayFields,
    productPrice,
    selectedProduct,
    swatchColorOptions,
    showColorSwatch,
    setProductSelected: setProductDimension,
    isTypeCashOrCharity,
    initiallySelectedProduct
  };
};
